import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  FileText,
  Eye,
  Pencil,
  Trash2,
  Sparkles,
  Loader2,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
  Lightbulb,
  Image as ImageIcon,
  RefreshCw,
  Send,
  Wand2,
} from 'lucide-react';

const BlogPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  
  // Generate dialog
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    topic: '',
    keywords: ''
  });
  
  // Topic suggestions
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  
  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  
  // Improve dialog
  const [improveOpen, setImproveOpen] = useState(false);
  const [improveInstructions, setImproveInstructions] = useState('');
  const [improveLoading, setImproveLoading] = useState(false);
  
  // Preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewArticle, setPreviewArticle] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, [statusFilter]);

  const fetchArticles = async () => {
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const response = await blogAPI.getArticles(status);
      setArticles(response.data.articles);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerateLoading(true);
    
    try {
      const keywords = generateForm.keywords
        ? generateForm.keywords.split(',').map(k => k.trim()).filter(k => k)
        : [];
      
      const response = await blogAPI.generateArticle(generateForm.topic, keywords);
      toast.success('Article généré avec succès !');
      setGenerateOpen(false);
      setGenerateForm({ topic: '', keywords: '' });
      fetchArticles();
      
      // Ouvrir l'article en édition
      setEditingArticle(response.data);
      setEditOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleFetchSuggestions = async () => {
    setSuggestionsLoading(true);
    setSuggestionsOpen(true);
    
    try {
      const response = await blogAPI.suggestTopics(5);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error('Erreur lors de la récupération des suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setGenerateForm({
      topic: suggestion.title,
      keywords: (suggestion.keywords || []).join(', ')
    });
    setSuggestionsOpen(false);
    setGenerateOpen(true);
  };

  const handleEdit = (article) => {
    setEditingArticle({ ...article });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    
    try {
      await blogAPI.updateArticle(editingArticle.id, {
        title: editingArticle.title,
        meta_description: editingArticle.meta_description,
        keywords: editingArticle.keywords,
        content: editingArticle.content,
        excerpt: editingArticle.excerpt,
        featured_image: editingArticle.featured_image
      });
      toast.success('Article mis à jour');
      setEditOpen(false);
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setEditLoading(false);
    }
  };

  const handleImprove = async () => {
    if (!improveInstructions.trim()) {
      toast.error('Veuillez entrer des instructions');
      return;
    }
    
    setImproveLoading(true);
    
    try {
      const response = await blogAPI.improveArticle(editingArticle.id, improveInstructions);
      toast.success('Article amélioré avec succès !');
      setEditingArticle(response.data);
      setImproveOpen(false);
      setImproveInstructions('');
    } catch (error) {
      toast.error('Erreur lors de l\'amélioration');
    } finally {
      setImproveLoading(false);
    }
  };

  const handlePublish = async (articleId) => {
    try {
      await blogAPI.publishArticle(articleId);
      toast.success('Article publié !');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const handleUnpublish = async (articleId) => {
    try {
      await blogAPI.unpublishArticle(articleId);
      toast.success('Article dépublié');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la dépublication');
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    
    try {
      await blogAPI.deleteArticle(articleId);
      toast.success('Article supprimé');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePreview = (article) => {
    setPreviewArticle(article);
    setPreviewOpen(true);
  };

  const handleAutoGenerate = async () => {
    try {
      toast.info('Génération automatique en cours...');
      const response = await blogAPI.triggerAutoGenerate();
      toast.success(response.data.message);
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la génération automatique');
    }
  };

  const filteredArticles = articles.filter((article) =>
    article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.topic?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Publié</Badge>;
      case 'draft':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Brouillon</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="blog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Blog & SEO
          </h1>
          <p className="text-slate-500 mt-1">{total} articles • Génération IA automatique</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleFetchSuggestions}
            data-testid="suggest-topics-btn"
          >
            <Lightbulb className="w-4 h-4 mr-2" />
            Suggestions IA
          </Button>
          <Button
            variant="outline"
            onClick={handleAutoGenerate}
            data-testid="auto-generate-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Générer auto
          </Button>
          <Button
            className="bg-orange-500 hover:bg-orange-600"
            onClick={() => setGenerateOpen(true)}
            data-testid="new-article-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Générer article
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-articles-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="published">Publiés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Articles Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredArticles.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun article pour le moment</p>
              <p className="text-sm mt-2">Commencez par générer votre premier article avec l'IA</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Article</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Statut</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Mots-clés</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredArticles.map((article) => (
                    <TableRow key={article.id} data-testid={`article-row-${article.id}`}>
                      <TableCell>
                        <div className="max-w-md">
                          <p className="font-medium text-slate-800 truncate">{article.title || 'Sans titre'}</p>
                          <p className="text-sm text-slate-500 truncate">{article.meta_description || article.excerpt}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(article.status)}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {(article.keywords || []).slice(0, 3).map((kw, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
                          ))}
                          {(article.keywords || []).length > 3 && (
                            <Badge variant="outline" className="text-xs">+{article.keywords.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 hidden lg:table-cell">
                        {article.published_at ? formatDate(article.published_at) : formatDate(article.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handlePreview(article)}
                            title="Aperçu"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(article)}
                            title="Modifier"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {article.status === 'draft' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600"
                              onClick={() => handlePublish(article.id)}
                              title="Publier"
                            >
                              <Globe className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-yellow-600"
                              onClick={() => handleUnpublish(article.id)}
                              title="Dépublier"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500"
                            onClick={() => handleDelete(article.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Dialog */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              <Sparkles className="w-5 h-5 inline mr-2 text-orange-500" />
              Générer un article avec l'IA
            </DialogTitle>
            <DialogDescription>
              L'IA va créer un article SEO optimisé sur le sujet de votre choix.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="topic">Sujet de l'article *</Label>
              <Input
                id="topic"
                value={generateForm.topic}
                onChange={(e) => setGenerateForm({ ...generateForm, topic: e.target.value })}
                placeholder="Ex: Comment choisir sa mini-pelle ?"
                required
                data-testid="topic-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="keywords">Mots-clés (optionnel)</Label>
              <Input
                id="keywords"
                value={generateForm.keywords}
                onChange={(e) => setGenerateForm({ ...generateForm, keywords: e.target.value })}
                placeholder="mini-pelle, Liugong, BTP, La Réunion"
                data-testid="keywords-input"
              />
              <p className="text-xs text-slate-500">Séparez les mots-clés par des virgules</p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setGenerateOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600"
                disabled={generateLoading}
                data-testid="generate-btn"
              >
                {generateLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Générer
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Suggestions Dialog */}
      <Dialog open={suggestionsOpen} onOpenChange={setSuggestionsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              <Lightbulb className="w-5 h-5 inline mr-2 text-yellow-500" />
              Suggestions de sujets
            </DialogTitle>
            <DialogDescription>
              L'IA vous propose des sujets tendances pour votre blog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {suggestionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-500">Recherche de sujets...</span>
              </div>
            ) : suggestions.length === 0 ? (
              <p className="text-center text-slate-500 py-4">Aucune suggestion disponible</p>
            ) : (
              suggestions.map((suggestion, idx) => (
                <Card
                  key={idx}
                  className="cursor-pointer hover:bg-slate-50 transition"
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800">{suggestion.title}</p>
                    {suggestion.description && (
                      <p className="text-sm text-slate-500 mt-1">{suggestion.description}</p>
                    )}
                    {suggestion.keywords && suggestion.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {suggestion.keywords.map((kw, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              <Pencil className="w-5 h-5 inline mr-2" />
              Modifier l'article
            </DialogTitle>
          </DialogHeader>
          {editingArticle && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Titre SEO</Label>
                <Input
                  id="edit-title"
                  value={editingArticle.title || ''}
                  onChange={(e) => setEditingArticle({ ...editingArticle, title: e.target.value })}
                  data-testid="edit-title-input"
                />
                <p className="text-xs text-slate-500">{(editingArticle.title || '').length}/60 caractères</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-meta">Meta description</Label>
                <Textarea
                  id="edit-meta"
                  value={editingArticle.meta_description || ''}
                  onChange={(e) => setEditingArticle({ ...editingArticle, meta_description: e.target.value })}
                  rows={2}
                  data-testid="edit-meta-input"
                />
                <p className="text-xs text-slate-500">{(editingArticle.meta_description || '').length}/160 caractères</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-keywords">Mots-clés</Label>
                <Input
                  id="edit-keywords"
                  value={(editingArticle.keywords || []).join(', ')}
                  onChange={(e) => setEditingArticle({
                    ...editingArticle,
                    keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                  })}
                  data-testid="edit-keywords-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-excerpt">Extrait</Label>
                <Textarea
                  id="edit-excerpt"
                  value={editingArticle.excerpt || ''}
                  onChange={(e) => setEditingArticle({ ...editingArticle, excerpt: e.target.value })}
                  rows={2}
                  data-testid="edit-excerpt-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-image">Image à la une (URL)</Label>
                <Input
                  id="edit-image"
                  value={editingArticle.featured_image || ''}
                  onChange={(e) => setEditingArticle({ ...editingArticle, featured_image: e.target.value })}
                  placeholder="https://..."
                  data-testid="edit-image-input"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-content">Contenu (HTML)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setImproveOpen(true)}
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Améliorer avec l'IA
                  </Button>
                </div>
                <Textarea
                  id="edit-content"
                  value={editingArticle.content || ''}
                  onChange={(e) => setEditingArticle({ ...editingArticle, content: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                  data-testid="edit-content-input"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  className="bg-slate-800 hover:bg-slate-900"
                  disabled={editLoading}
                  data-testid="save-edit-btn"
                >
                  {editLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Improve Dialog */}
      <Dialog open={improveOpen} onOpenChange={setImproveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Wand2 className="w-5 h-5 inline mr-2 text-purple-500" />
              Améliorer avec l'IA
            </DialogTitle>
            <DialogDescription>
              Donnez des instructions pour améliorer l'article.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              value={improveInstructions}
              onChange={(e) => setImproveInstructions(e.target.value)}
              placeholder="Ex: Ajoute plus de détails sur les avantages des mini-pelles Liugong, améliore le SEO, rends le ton plus engageant..."
              rows={4}
              data-testid="improve-instructions-input"
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setImproveOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleImprove}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={improveLoading}
                data-testid="improve-btn"
              >
                {improveLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4 mr-2" />
                )}
                Améliorer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              <Eye className="w-5 h-5 inline mr-2" />
              Aperçu de l'article
            </DialogTitle>
          </DialogHeader>
          {previewArticle && (
            <div className="mt-4 prose prose-slate max-w-none">
              <h1>{previewArticle.title}</h1>
              <p className="text-slate-500 italic">{previewArticle.meta_description}</p>
              {previewArticle.featured_image && (
                <img
                  src={previewArticle.featured_image}
                  alt={previewArticle.title}
                  className="rounded-lg max-h-64 object-cover w-full"
                />
              )}
              <div dangerouslySetInnerHTML={{ __html: previewArticle.content }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogPage;
